/**
 * PURE table-model builder for the unified Table & Matrix family.
 *
 * No Angular dependency — takes capabilities + flat rows and returns a plain
 * `TableModel` (columns + rows + conditional-format + subtotals/grand-total), so
 * all shaping logic is unit-testable. The component just renders the model as an
 * HTML/mat-table. This is the table analog of `buildBarColumnOption`.
 *
 * `tableType` (via `typeFlags`) selects behaviour (no name-string checks):
 *   normal    → pick columns, optional group-by subtotals + grand total.
 *   editable  → same column model + per-column control/validation (edit UI is the
 *               component's job; this builder just shapes columns/rows).
 *   drilldown → handled by the existing lib-drill-down path, NOT this builder
 *               (the component renders drill-down via its own widget data).
 *
 * EVERY styling/format constant is read from `capabilities.chartDefaults` (from
 * JSON `tableMatrixChartDefaults`). FALLBACK is a last-resort safety net ONLY.
 */
import type {
  TableMatrixCapabilities,
  TableMatrixColumn,
  TableMatrixConditionalRule,
  TableModel,
  TableModelCell,
  TableModelColumn,
  TableModelColumnGroup,
  TableModelRow,
} from './unified-table-matrix-chart.types';
import { reorderValues } from './drilldown-client-tree.utils';

type Row = Record<string, unknown>;

/** LAST-RESORT safety net ONLY — authoritative values live in the JSON
 *  (`tableMatrixChartDefaults`). Used when both the per-tile capability AND the
 *  config are absent, so a broken/missing config never throws. */
const FALLBACK = {
  defaultColumnWidth: 140,
  totalGroupLabel: 'Total',
  grandTotalLabel: 'Grand Total',
};

/** Numeric coercion tolerant of "1,234"/"$1.2k" → numeric part; non-numeric → null. */
function toNum(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string') {
    const n = Number(value.replace(/[^0-9.eE+-]/g, ''));
    return Number.isFinite(n) && value.trim() !== '' ? n : null;
  }
  return null;
}

function rowHasKey(row: Row | undefined, key: string | undefined): boolean {
  return !!row && !!key && Object.prototype.hasOwnProperty.call(row, key);
}

function resolveRowValue(raw: Row, field: string, label?: string): unknown {
  if (rowHasKey(raw, field)) return raw[field];
  if (rowHasKey(raw, label)) return raw[label as string];
  return raw[field];
}

/** Aggregate a numeric column of rows by the given mode. */
function aggregate(values: number[], mode: string): number {
  if (!values.length) return 0;
  switch (mode) {
    case 'avg': return values.reduce((a, b) => a + b, 0) / values.length;
    case 'count': return values.length;
    case 'distinct': return new Set(values).size;
    case 'min': return Math.min(...values);
    case 'max': return Math.max(...values);
    case 'sum':
    default: return values.reduce((a, b) => a + b, 0);
  }
}

/** The active aggregation mode (defaults to 'sum' when unset / 'none'). */
function aggMode(capabilities: TableMatrixCapabilities): string {
  const a = capabilities.aggregation;
  return a && a !== 'none' ? a : 'sum';
}

/** Sum/aggregate one numeric field across a set of rows. */
function aggField(rows: Row[], field: string, mode: string): number {
  const vals = rows.map((r) => toNum(r[field])).filter((n): n is number => n !== null);
  return aggregate(vals, mode);
}

/** Lightweight number formatter (kept local so the builder has no Angular dep). */
function formatValue(value: number, capabilities: TableMatrixCapabilities): string {
  if (!Number.isFinite(value)) return '-';
  const scale = capabilities.numberScale || 'none';
  const fmt = capabilities.numberFormat || 'NONE';
  const decimals = Math.max(0, Math.min(6, Math.floor(capabilities.decimals ?? 1)));
  let scaled = value;
  let suffix = '';
  switch (scale) {
    case 'thousands': case 'SCALE_THOUSANDS': scaled = value / 1e3; suffix = 'K'; break;
    case 'millions': case 'MILLIONS': scaled = value / 1e6; suffix = 'M'; break;
    case 'billions': case 'BILLIONS': scaled = value / 1e9; suffix = 'B'; break;
    case 'compact': case 'COMPACT': {
      const abs = Math.abs(value);
      if (abs >= 1e9) { scaled = value / 1e9; suffix = 'B'; }
      else if (abs >= 1e6) { scaled = value / 1e6; suffix = 'M'; }
      else if (abs >= 1e3) { scaled = value / 1e3; suffix = 'K'; }
      break;
    }
    default: break;
  }
  if (fmt === 'PERCENT' || fmt === 'percent') return `${(value * 100).toFixed(decimals)}%`;
  const useCommas = fmt === 'THOUSANDS' || fmt === 'thousands' || suffix === '';
  const body = useCommas && !suffix
    ? scaled.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: decimals })
    : scaled.toFixed(decimals);
  return `${body}${suffix}`;
}

/** Format one cell's display text by column type. */
function displayFor(value: unknown, col: TableModelColumn, capabilities: TableMatrixCapabilities): string {
  if (value === undefined || value === null || value === '') return '';
  if (col.type === 'number' || col.type === 'currency' || col.type === 'percent') {
    const n = toNum(value);
    if (n === null) return String(value);
    const body = formatValue(n, capabilities);
    if (col.type === 'currency') return `$${body}`;
    if (col.type === 'percent' && !(capabilities.numberFormat || '').toUpperCase().includes('PERCENT')) return `${body}%`;
    return body;
  }
  return String(value);
}

/** Whether a value satisfies a conditional-format rule. */
function ruleMatches(value: unknown, rule: TableMatrixConditionalRule): boolean {
  const n = toNum(value);
  if (n === null) return false;
  switch (rule.operator) {
    case '>': return n > rule.value;
    case '>=': return n >= rule.value;
    case '<': return n < rule.value;
    case '<=': return n <= rule.value;
    case '=': return n === rule.value;
    case '!=': return n !== rule.value;
    case 'between': return rule.value2 !== undefined && n >= rule.value && n <= rule.value2;
    default: return false;
  }
}

/** Resolve the conditional-format styles for a cell (first matching rule wins). */
function conditionalStyle(field: string, value: unknown, rules: TableMatrixConditionalRule[]): { background?: string; color?: string } {
  for (const rule of rules) {
    if (rule.field === field && ruleMatches(value, rule)) {
      return { background: rule.background, color: rule.color };
    }
  }
  return {};
}

/** Normalize an editable column's inline dropdown options (bare strings OR
 *  {value,label}) into a uniform {value,label} list. */
function resolveColumnOptions(
  opts?: Array<{ value: string; label?: string } | string>,
): Array<{ value: string; label: string }> | undefined {
  if (!Array.isArray(opts) || !opts.length) return undefined;
  return opts.map((o) =>
    typeof o === 'string'
      ? { value: o, label: o }
      : { value: String(o.value), label: String(o.label ?? o.value) },
  );
}

/** A stable per-row id for change-tracking (uses an existing id-like field when
 *  present, else the row's ordinal — deterministic so re-renders keep the key). */
function rowIdFor(raw: Row, index: number): string {
  const cand = raw['id'] ?? raw['rowId'] ?? raw['_id'] ?? raw['uuid'];
  return cand !== undefined && cand !== null && cand !== '' ? String(cand) : `r${index}`;
}

/** Distinct values of a column in first-seen order. */
function distinct(rows: Row[], field: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  rows.forEach((r) => {
    const k = String(r[field] ?? '');
    if (!seen.has(k)) { seen.add(k); out.push(k); }
  });
  return out;
}

/**
 * Build the client-side TableModel from flat rows + capabilities.
 * Returns `{ columns:[], rows:[], empty:true }` when config is incomplete.
 */
export function buildTableModel(rows: Array<Record<string, unknown>>, capabilities: TableMatrixCapabilities): TableModel {
  const safeRows = Array.isArray(rows) ? rows : [];

  // Dispatch to the shape-specific builder by type flag (falls through to the
  // flat Normal/Editable model below). typeFlags is config-resolved; name check
  // is a fallback when flags are absent.
  const f = capabilities.typeFlags || {};
  const t = capabilities.tableType;
  if (f['matrix'] || t === 'matrix') return buildMatrixModel(safeRows, capabilities);
  if (f['pivot'] || t === 'pivot') return buildPivotModel(safeRows, capabilities);
  if (f['grouped'] || t === 'grouped') return buildGroupedModel(safeRows, capabilities);
  if (f['treegrid'] || t === 'treegrid') return buildTreeGridModel(safeRows, capabilities);
  if (f['financial'] || t === 'financial') return buildFinancialModel(safeRows, capabilities);

  const d = capabilities.chartDefaults || {};
  const defaultWidth = (d.defaultColumnWidth as number) ?? FALLBACK.defaultColumnWidth;

  // Column model: use configured columns, else infer from the first row's keys.
  // If NONE of the configured fields exist on the actual rows, the config is
  // stale (e.g. built against a different response shape) — fall back to the
  // row keys rather than rendering a table full of blank cells.
  const configured = Array.isArray(capabilities.columns) ? capabilities.columns : [];
  const rowKeys = new Set<string>();
  safeRows.forEach((row) => {
    Object.keys(row || {}).forEach((key) => rowKeys.add(key));
  });
  const configuredMatchesData = configured.some((c) => rowKeys.has(c.field) || (!!c.label && rowKeys.has(c.label)));
  // Fallback path: the response rows are keyed differently from the configured
  // `field`s (e.g. the backend returns display-name keys). Don't drop the whole
  // column config — re-attach each configured column's settings (label,
  // editable/control, validation) to the inferred key by matching the key
  // against the configured field OR label, and point `field` at the real key.
  const cfgByField = new Map(configured.map((c) => [c.field, c]));
  const cfgByLabel = new Map(configured.filter((c) => c.label).map((c) => [String(c.label), c]));
  const columnDefs: TableMatrixColumn[] = configured.length && (configuredMatchesData || !safeRows.length)
    ? configured
    : (safeRows[0]
        ? [
            ...Object.keys(safeRows[0]).map((field) => {
              const cfg = cfgByField.get(field) || cfgByLabel.get(field);
              return cfg ? { ...cfg, field } : { field };
            }),
            // Action columns have no row key to be inferred from, so this
            // row-key fallback would silently drop them. Keep them (in their
            // configured order relative to each other) at the tail.
            ...configured.filter((c) => c.isAction === true),
          ]
        : []);

  if (!columnDefs.length) return { columns: [], rows: [], empty: true };

  // Editable tables need a control per column to render an <input> cell. A
  // column with an explicit `editable: false` gets NO control → renders
  // read-only (the per-column "lock this column" feature); absent means
  // editable, so older saved configs keep their all-columns-editable behaviour
  // and truly unconfigured (inferred) columns default to a plain text control.
  const isEditableTable = capabilities.typeFlags?.['editable'] === true || capabilities.tableType === 'editable';
  const isCellEditingTable = capabilities.cellEditing === true;

  const columns: TableModelColumn[] = columnDefs.map((c) => {
    const sampleValue = safeRows.length ? resolveRowValue(safeRows[0], c.field, c.label) : undefined;
    // An ACTION column holds a button, not data: it must never pick up an
    // editable control (it would render an <input> over the button) and must
    // never be type-inferred from a row value it has none of.
    const isActionColumn = c.isAction === true;
    const isReadonlyEditableColumn = !isActionColumn
      && ((isEditableTable && c.editable === false) || (isCellEditingTable && c.editable !== true));
    // A locked/read-only column inside an editable table renders through
    // `display` instead of an <input>. If we infer `number` here, identifiers
    // and code-like values can be coerced/formatted differently from what the
    // user saw while the column was editable. Only numeric-format readonly
    // columns when the config explicitly asks for a numeric type.
    const type = isActionColumn
      ? 'text'
      : (c.type || (isReadonlyEditableColumn ? 'text' : (safeRows.length && toNum(sampleValue) !== null ? 'number' : 'text')));
    const defaultAlign = isActionColumn
      ? 'center'
      : (isEditableTable
          ? 'start'
          : (type === 'number' || type === 'currency' || type === 'percent' ? 'end' : 'start'));
    return {
      key: c.field,
      // An action column's header is its configured label or nothing — the
      // field is a synthetic `__action_*` key and must never be shown.
      label: isActionColumn ? (c.label || '') : (c.label || c.field),
      type,
      align: c.align || defaultAlign,
      width: c.width || defaultWidth,
      frozen: c.frozen === true || (capabilities.freezeFirstColumn === true && c.field === columnDefs[0].field),
      // Presentation-only: the component drops these from `visibleColumns` but
      // keeps them in `model.columns`, so the save payload still carries them.
      hidden: c.hidden === true,
      control: isActionColumn
        ? undefined
        : (c.editable === false ? undefined : (c.control || (isEditableTable ? 'text' : undefined))),
      saveAllTrigger: c.saveAllTrigger === true,
      mandatory: c.mandatory === true,
      // Action column — the component renders a button for these in every row.
      isAction: isActionColumn,
      buttonIcon: c.buttonIcon,
      actionKind: c.actionKind,
      popupComponentId: c.popupComponentId,
      popupData: c.popupData,
      actionUrl: c.actionUrl,
      actionMethod: c.actionMethod,
      actionBody: c.actionBody,
      actionRowsPath: c.actionRowsPath,
      actionRowsSortBy: c.actionRowsSortBy,
      actionRowsSortDir: c.actionRowsSortDir,
      actionMaxRows: c.actionMaxRows,
      actionRowsColumns: c.actionRowsColumns,
      actionTitle: c.actionTitle,
      actionConfirm: c.actionConfirm,
      actionRefresh: c.actionRefresh === true,
      optionsKey: c.optionsKey,
      options: resolveColumnOptions(c.options),
      lookupUrl: c.lookupUrl,
      hrefTemplate: c.hrefTemplate,
      buttonLabel: c.buttonLabel,
      buttonAction: c.buttonAction,
      required: c.required,
      min: c.min,
      max: c.max,
      regex: c.regex,
      unique: c.unique,
      requiredWhen: c.requiredWhen,
      gteField: c.gteField,
      lteField: c.lteField,
    };
  });

  const rules = Array.isArray(capabilities.conditionalRules) ? capabilities.conditionalRules : [];
  const numericCols = columns.filter((c) => c.type === 'number' || c.type === 'currency' || c.type === 'percent');

  // Build one rendered row (with per-cell display + conditional styles). Data
  // rows carry a stable rowId (editable change-tracking); the raw row's own
  // `__state`/`__rowId` (set by client-side add/edit) is surfaced onto the model.
  const makeRow = (raw: Row, kind: TableModelRow['kind'], groupLabel?: string, index = 0): TableModelRow => {
    const cells: Record<string, TableModelCell> = {};
    columns.forEach((col) => {
      const value = resolveRowValue(raw, col.key, col.label);
      const style = kind === 'data' ? conditionalStyle(col.key, value, rules) : {};
      cells[col.key] = { value, display: displayFor(value, col, capabilities), background: style.background, color: style.color };
    });
    const base: TableModelRow = { cells, raw: kind === 'data' ? raw : undefined, kind, groupLabel };
    if (kind === 'data') {
      base.rowId = (raw['__rowId'] as string) || rowIdFor(raw, index);
      const st = raw['__state'] as TableModelRow['state'] | undefined;
      if (st) base.state = st;
    }
    return base;
  };

  const modelRows: TableModelRow[] = [];
  const groupBy = capabilities.groupBy && columns.some((c) => c.key === capabilities.groupBy) ? capabilities.groupBy : '';
  const wantSubtotals = capabilities.showSubtotals === true && !!groupBy;

  if (groupBy) {
    // Group rows; optional subtotal per group.
    distinct(safeRows, groupBy).forEach((g) => {
      const groupRows = safeRows.filter((r) => String(r[groupBy] ?? '') === g);
      groupRows.forEach((r, gi) => modelRows.push(makeRow(r, 'data', undefined, gi)));
      if (wantSubtotals) {
        const subtotal: Row = { [columns[0].key]: `${g} ${FALLBACK.totalGroupLabel}` };
        numericCols.forEach((col) => {
          const vals = groupRows.map((r) => toNum(r[col.key])).filter((n): n is number => n !== null);
          subtotal[col.key] = aggregate(vals, capabilities.aggregation && capabilities.aggregation !== 'none' ? capabilities.aggregation : 'sum');
        });
        modelRows.push({ ...makeRow(subtotal, 'subtotal', g), groupLabel: g });
      }
    });
  } else {
    safeRows.forEach((r, i) => modelRows.push(makeRow(r, 'data', undefined, i)));
  }

  // Grand total row.
  if (capabilities.showGrandTotal === true && numericCols.length) {
    const grand: Row = { [columns[0].key]: FALLBACK.grandTotalLabel };
    numericCols.forEach((col) => {
      const vals = safeRows.map((r) => toNum(r[col.key])).filter((n): n is number => n !== null);
      grand[col.key] = aggregate(vals, capabilities.aggregation && capabilities.aggregation !== 'none' ? capabilities.aggregation : 'sum');
    });
    modelRows.push(makeRow(grand, 'grandtotal'));
  }

  // "Empty" means the table has NO COLUMNS to render (config incomplete). With
  // columns but zero rows we still render the header + a "no data" body, so the
  // user sees their configured columns instead of the misleading "Map columns".
  return { columns, rows: modelRows, empty: columns.length === 0, shape: 'flat' };
}

// ─────────────────────────────────────────────────────────────────────────────
//  Shared helpers for the shape-specific builders below
// ─────────────────────────────────────────────────────────────────────────────

/** Build a TableModelColumn from a field key + options (type inferred from a
 *  sample value when not given). Shared by every builder. */
function makeColumn(
  field: string,
  opts: Partial<TableModelColumn>,
  sampleValue: unknown,
  defaultWidth: number,
): TableModelColumn {
  const type = (opts.type as TableModelColumn['type'])
    || (toNum(sampleValue) !== null ? 'number' : 'text');
  return {
    key: field,
    label: opts.label || field,
    type,
    align: opts.align || (type === 'number' || type === 'currency' || type === 'percent' ? 'end' : 'start'),
    width: opts.width || defaultWidth,
    frozen: opts.frozen === true,
    hidden: opts.hidden === true,
    control: opts.control,
    columnGroup: opts.columnGroup,
  };
}

/** Render a cell (display + conditional style) for a value in a column. */
function makeCell(
  value: unknown,
  col: TableModelColumn,
  capabilities: TableMatrixCapabilities,
  rules: TableMatrixConditionalRule[],
  applyRules: boolean,
): TableModelCell {
  const style = applyRules ? conditionalStyle(col.key, value, rules) : {};
  return { value, display: displayFor(value, col, capabilities), background: style.background, color: style.color };
}

/** Assemble a full row from a flat record over the given columns. */
function rowFrom(
  raw: Row,
  columns: TableModelColumn[],
  capabilities: TableMatrixCapabilities,
  rules: TableMatrixConditionalRule[],
  kind: TableModelRow['kind'],
  extra: Partial<TableModelRow> = {},
): TableModelRow {
  const cells: Record<string, TableModelCell> = {};
  columns.forEach((col) => { cells[col.key] = makeCell(raw[col.key], col, capabilities, rules, kind === 'data'); });
  return { cells, raw: kind === 'data' ? raw : undefined, kind, ...extra };
}

const defWidth = (c: TableMatrixCapabilities): number =>
  ((c.chartDefaults?.defaultColumnWidth as number) ?? FALLBACK.defaultColumnWidth);

// ─────────────────────────────────────────────────────────────────────────────
//  MATRIX — one row dimension down, one column dimension across (cross-tab)
// ─────────────────────────────────────────────────────────────────────────────
export function buildMatrixModel(safeRows: Row[], capabilities: TableMatrixCapabilities): TableModel {
  const rules = Array.isArray(capabilities.conditionalRules) ? capabilities.conditionalRules : [];
  const dw = defWidth(capabilities);
  const rowField = capabilities.rowField
    || capabilities.columns?.[0]?.field
    || (safeRows[0] ? Object.keys(safeRows[0])[0] : '');
  const colField = capabilities.columnField || '';
  const valField = capabilities.valueField || '';
  if (!rowField || !colField || !valField || !safeRows.length) {
    return { columns: [], rows: [], empty: true, shape: 'matrix' };
  }
  // Resolve configured fields to the row keys that ACTUALLY exist (the
  // backend may echo display-name keys — see fieldKeyResolver).
  const resolve = fieldKeyResolver(safeRows);
  const rowFieldR = resolve(rowField);
  const colFieldR = resolve(colField);
  const valFieldR = resolve(valField);
  const colValues = distinct(safeRows, colFieldR);
  // All-numeric column values (years) sort ascending; others keep data order.
  if (colValues.length > 1 && colValues.every((v) => v !== '' && Number.isFinite(Number(v)))) {
    colValues.sort((a, b) => Number(a) - Number(b));
  }
  const mode = aggMode(capabilities);

  // Columns: the row-dimension label column + one value column per column-value.
  const columns: TableModelColumn[] = [
    makeColumn(rowFieldR, { label: humanizeField(rowField), frozen: capabilities.freezeFirstColumn !== false, type: 'text' }, safeRows[0][rowFieldR], dw),
    ...colValues.map((cv) => makeColumn(`__m_${cv}`, { label: cv || '(blank)', type: 'number', columnGroup: colField }, safeRows.find((r) => String(r[colFieldR] ?? '') === cv)?.[valFieldR], dw)),
  ];
  const columnGroups: TableModelColumnGroup[] = [{ label: colField, keys: colValues.map((cv) => `__m_${cv}`) }];

  // One row per distinct row-dimension value; each cell = aggregated measure.
  const rowValues = distinct(safeRows, rowFieldR);
  const modelRows: TableModelRow[] = rowValues.map((rv) => {
    const rowRows = safeRows.filter((r) => String(r[rowFieldR] ?? '') === rv);
    const raw: Row = { [rowFieldR]: rv };
    colValues.forEach((cv) => {
      const cellRows = rowRows.filter((r) => String(r[colFieldR] ?? '') === cv);
      raw[`__m_${cv}`] = cellRows.length ? aggField(cellRows, valFieldR, mode) : '';
    });
    return rowFrom(raw, columns, capabilities, rules, 'data');
  });

  // Grand total row across all row-values, per column-value.
  if (capabilities.showGrandTotal === true) {
    const raw: Row = { [rowFieldR]: FALLBACK.grandTotalLabel };
    colValues.forEach((cv) => {
      const cellRows = safeRows.filter((r) => String(r[colFieldR] ?? '') === cv);
      raw[`__m_${cv}`] = aggField(cellRows, valFieldR, mode);
    });
    modelRows.push(rowFrom(raw, columns, capabilities, rules, 'grandtotal'));
  }

  return { columns, rows: modelRows, empty: !modelRows.length, columnGroups, shape: 'matrix' };
}

// ─────────────────────────────────────────────────────────────────────────────
//  PIVOT — N row fields × N column fields × a measure (generalised matrix)
// ─────────────────────────────────────────────────────────────────────────────
/** "net_sales" → "Net Sales" for generated matrix/pivot column labels. */
const humanizeField = (f: string): string =>
  String(f || '').replace(/[_-]+/g, ' ').trim().replace(/\S+/g, (w) => w.charAt(0).toUpperCase() + w.slice(1));

/** Case/punctuation-insensitive row-key resolution — the backend may echo rows
 *  keyed by DISPLAY NAMES ("Fiscal Year") instead of the configured field
 *  names (fiscal_year), which left the matrix/pivot reading undefined
 *  everywhere ("(blank)" columns, one empty row). Same field-or-label
 *  tolerance the flat table's resolveRowValue applies. */
const normFieldKey = (s: string): string => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
const fieldKeyResolver = (rows: Row[]): ((f: string) => string) => {
  // Union the keys of MANY rows, not just the first — backends often omit
  // null fields per row, so row #1 alone can be missing whole columns.
  const exact = new Set<string>();
  const byNorm = new Map<string, string>();
  (rows || []).slice(0, 200).forEach((r) => Object.keys(r || {}).forEach((k) => {
    if (!exact.has(k)) {
      exact.add(k);
      const n = normFieldKey(k);
      if (!byNorm.has(n)) byNorm.set(n, k);
    }
  }));
  return (f: string): string => (exact.has(f) ? f : (byNorm.get(normFieldKey(f)) ?? f));
};

export function buildPivotModel(safeRows: Row[], capabilities: TableMatrixCapabilities): TableModel {
  const rules = Array.isArray(capabilities.conditionalRules) ? capabilities.conditionalRules : [];
  const dw = defWidth(capabilities);
  const rowFields = (capabilities.rowFields || []).filter(Boolean);
  const colFields = (capabilities.columnFields || []).filter(Boolean);
  // MULTI-MEASURE: `valueFields` fans EVERY listed measure out per column
  // value (e.g. fiscal_year across → "2024 Net Sales | 2025 Net Sales | …" +
  // "2024 Net Quantity | …"), matching the classic Excel year-block layout.
  // Falls back to the single `valueField` — and a single measure keeps the
  // ORIGINAL column keys/labels/group, so existing pivot tiles render unchanged.
  const listed = (capabilities.valueFields || []).filter(Boolean);
  const valFields = listed.length ? listed : (capabilities.valueField ? [capabilities.valueField] : []);
  if (!rowFields.length || !colFields.length || !valFields.length || !safeRows.length) {
    return { columns: [], rows: [], empty: true, shape: 'pivot' };
  }
  const multi = valFields.length > 1;
  const keyFor = (ck: string, vf: string) => (multi ? `__p_${ck}__${vf}` : `__p_${ck}`);
  // Sub-header = the column VALUE only ("2024"); the measure name is already
  // the spanning group band above it (one group per measure when multi), so
  // repeating it ("2024 Net Sales") just made the header noisy. Exports still
  // prefix the group name so the flat file stays unambiguous.
  const labelFor = (ck: string, _vf: string) => (ck || '(blank)');
  const mode = aggMode(capabilities);
  // Aggregation "None (raw rows)": ONE table row per response row — each row's
  // value lands under its own column value, nothing is combined at the leaf
  // level. Parent (group) rows and the grand total still roll up.
  const rawRows = capabilities.aggregation === 'none';
  // Resolve every configured field to the row key that ACTUALLY exists (the
  // backend may echo display-name keys — see fieldKeyResolver).
  const resolve = fieldKeyResolver(safeRows);
  const rowFieldsR = rowFields.map(resolve);
  const colFieldsR = colFields.map(resolve);
  const valFieldsR = valFields.map(resolve);
  // TWO OR MORE row fields → hierarchical rows (Excel-pivot style drill-down):
  // level 1 = first row field's values with aggregated cells, expandable to
  // its level-2 children, and so on — ONE indented label column instead of a
  // column per row field. A single row field keeps the flat one-row-per-value
  // shape unchanged.
  const hierarchical = rowFieldsR.length > 1;
  const labelKey = rowFieldsR[0];
  // Column keys = the joined distinct combination of the column fields.
  const colKeyOf = (r: Row) => colFieldsR.map((f) => String(r[f] ?? '')).join(' / ');
  const colKeys: string[] = [];
  const seenCK = new Set<string>();
  safeRows.forEach((r) => { const k = colKeyOf(r); if (!seenCK.has(k)) { seenCK.add(k); colKeys.push(k); } });
  // Explicit band order (e.g. "Mining, Marine, Total") + an alphabetical
  // fallback for whatever's left unlisted — same "Value order"/"Sort" pair
  // used everywhere else (Drill-Down row & column dimensions). All-numeric
  // column values (years) always sort ascending regardless, so the response's
  // first-seen order never interleaves them (2024, 2026, 2025).
  const pivotCaps = capabilities as { pivotColumnValueOrder?: string[]; pivotColumnValueOrderSort?: 'custom' | 'alpha-asc' | 'alpha-desc' };
  const reordered = reorderValues(colKeys, pivotCaps.pivotColumnValueOrder, pivotCaps.pivotColumnValueOrderSort);
  colKeys.length = 0;
  colKeys.push(...reordered);

  // Measure-major order: all column-values of measure 1, then measure 2, … —
  // the Excel "Qty block, then Net Sales block" reading order. Multi-measure
  // gets one spanning header group PER MEASURE; a single measure keeps the
  // original one-group-for-the-column-dimension header.
  const columns: TableModelColumn[] = [
    ...(hierarchical
      ? [makeColumn(labelKey, { label: rowFields.map(humanizeField).join(' / '), frozen: capabilities.freezeFirstColumn !== false, type: 'text' }, safeRows[0][labelKey], dw)]
      : rowFieldsR.map((f, i) => makeColumn(f, { label: humanizeField(rowFields[i]), frozen: i === 0 && capabilities.freezeFirstColumn !== false, type: 'text' }, safeRows[0][f], dw))),
    ...valFieldsR.flatMap((vf) => colKeys.map((ck) => makeColumn(keyFor(ck, vf), { label: labelFor(ck, vf), type: 'number', columnGroup: multi ? humanizeField(vf) : colFields.join(' / ') }, undefined, dw))),
  ];
  const columnGroups: TableModelColumnGroup[] = multi
    ? valFieldsR.map((vf) => ({ label: humanizeField(vf), keys: colKeys.map((ck) => keyFor(ck, vf)) }))
    : [{ label: colFields.join(' / '), keys: colKeys.map((ck) => keyFor(ck, valFieldsR[0])) }];

  const rowKeyOf = (r: Row) => rowFieldsR.map((f) => String(r[f] ?? '')).join('');
  const rowGroups: Record<string, Row[]> = {};
  const rowOrder: string[] = [];
  safeRows.forEach((r) => { const k = rowKeyOf(r); (rowGroups[k] ||= (rowOrder.push(k), [])).push(r); });

  /** Fill one output row's measure×column-value cells from its source rows. */
  const fillCells = (raw: Row, rows: Row[]): void => {
    colKeys.forEach((ck) => {
      const cellRows = rows.filter((r) => colKeyOf(r) === ck);
      valFieldsR.forEach((vf) => { raw[keyFor(ck, vf)] = cellRows.length ? aggField(cellRows, vf, mode) : ''; });
    });
  };

  const modelRows: TableModelRow[] = [];
  if (hierarchical) {
    // Drill-down rows: each level's value gets ONE row with its AGGREGATED
    // cells (parents show the roll-up of their children, like the drill-down
    // table); non-leaf rows carry the expand chevron via `expandable` +
    // rowKey/parentKey (the component's grouped/tree collapse machinery).
    const walk = (rows: Row[], level: number, parentKey: string): void => {
      const field = rowFieldsR[level];
      distinct(rows, field).forEach((g) => {
        const groupRows = rows.filter((r) => String(r[field] ?? '') === g);
        const key = `${parentKey}|${g}`;
        const leaf = level === rowFieldsR.length - 1;
        if (leaf && rawRows) {
          // Raw mode: every response row of this leaf value becomes its own
          // table row (same label, its single value under its column).
          groupRows.forEach((r, i) => {
            const raw: Row = { [labelKey]: g || '(blank)' };
            fillCells(raw, [r]);
            modelRows.push(rowFrom(raw, columns, capabilities, rules, 'data', {
              depth: level, rowKey: `${key}#${i}`, parentKey: parentKey || undefined,
            }));
          });
          return;
        }
        const raw: Row = { [labelKey]: g || '(blank)' };
        fillCells(raw, groupRows);
        modelRows.push(rowFrom(raw, columns, capabilities, rules, leaf ? 'data' : 'group', {
          depth: level,
          rowKey: key,
          parentKey: parentKey || undefined,
          ...(leaf ? {} : { expandable: true, expanded: capabilities.groupsExpanded !== false, groupLabel: g }),
        }));
        if (!leaf) walk(groupRows, level + 1, key);
      });
    };
    walk(safeRows, 0, '');
  } else {
    if (rawRows) {
      // Raw mode: one table row per response row, in response order.
      safeRows.forEach((r) => {
        const raw: Row = {};
        rowFieldsR.forEach((f) => { raw[f] = r[f]; });
        fillCells(raw, [r]);
        modelRows.push(rowFrom(raw, columns, capabilities, rules, 'data'));
      });
    } else {
      rowOrder.forEach((rk) => {
        const grp = rowGroups[rk];
        const raw: Row = {};
        rowFieldsR.forEach((f) => { raw[f] = grp[0][f]; });
        fillCells(raw, grp);
        modelRows.push(rowFrom(raw, columns, capabilities, rules, 'data'));
      });
    }
  }

  // Grand total row — aggregated PER GENERATED COLUMN (per measure per column
  // value, so e.g. each year totals independently).
  if (capabilities.showGrandTotal === true) {
    const raw: Row = { [rowFieldsR[0]]: FALLBACK.grandTotalLabel };
    colKeys.forEach((ck) => {
      const cellRows = safeRows.filter((r) => colKeyOf(r) === ck);
      valFieldsR.forEach((vf) => { raw[keyFor(ck, vf)] = aggField(cellRows, vf, mode); });
    });
    modelRows.push(rowFrom(raw, columns, capabilities, rules, 'grandtotal'));
  }

  return { columns, rows: modelRows, empty: !modelRows.length, columnGroups, shape: 'pivot' };
}

// ─────────────────────────────────────────────────────────────────────────────
//  GROUPED — multi-level row grouping with group-header rows + subtotals
// ─────────────────────────────────────────────────────────────────────────────
export function buildGroupedModel(safeRows: Row[], capabilities: TableMatrixCapabilities): TableModel {
  const rules = Array.isArray(capabilities.conditionalRules) ? capabilities.conditionalRules : [];
  const dw = defWidth(capabilities);
  const groupFields = (capabilities.groupByFields && capabilities.groupByFields.length
    ? capabilities.groupByFields
    : (capabilities.groupBy ? [capabilities.groupBy] : [])).filter(Boolean);
  const dataCols = Array.isArray(capabilities.columns) && capabilities.columns.length
    ? capabilities.columns.map((c) => c.field)
    : (safeRows[0] ? Object.keys(safeRows[0]) : []);
  if (!groupFields.length || !safeRows.length || !dataCols.length) {
    // No grouping configured → fall back to a flat model.
    return { ...buildTableModel(safeRows, { ...capabilities, typeFlags: {}, tableType: 'normal' }), shape: 'grouped' };
  }
  const columns: TableModelColumn[] = dataCols.map((f, i) =>
    makeColumn(f, { frozen: i === 0 && capabilities.freezeFirstColumn === true }, safeRows[0]?.[f], dw));
  const numericCols = columns.filter((c) => c.type === 'number' || c.type === 'currency' || c.type === 'percent');
  const mode = aggMode(capabilities);
  const wantSubtotals = capabilities.showSubtotals === true;
  const modelRows: TableModelRow[] = [];

  const walk = (rows: Row[], level: number, parentKey: string) => {
    const field = groupFields[level];
    distinct(rows, field).forEach((g) => {
      const groupRows = rows.filter((r) => String(r[field] ?? '') === g);
      const key = parentKey ? `${parentKey}${g}` : g;
      // Group-header row (label in the first column, indented by level).
      const header: Row = { [columns[0].key]: g };
      modelRows.push(rowFrom(header, columns, capabilities, rules, 'group', {
        depth: level, expandable: true, expanded: capabilities.groupsExpanded !== false, rowKey: key, parentKey: parentKey || undefined, groupLabel: g,
      }));
      if (level + 1 < groupFields.length) {
        walk(groupRows, level + 1, key);
      } else {
        groupRows.forEach((r) => modelRows.push(rowFrom(r, columns, capabilities, rules, 'data', { depth: level + 1, parentKey: key })));
      }
      if (wantSubtotals) {
        const sub: Row = { [columns[0].key]: `${g} ${FALLBACK.totalGroupLabel}` };
        numericCols.forEach((col) => { sub[col.key] = aggField(groupRows, col.key, mode); });
        modelRows.push(rowFrom(sub, columns, capabilities, rules, 'subtotal', { depth: level, parentKey: key, groupLabel: g }));
      }
    });
  };
  walk(safeRows, 0, '');

  if (capabilities.showGrandTotal === true && numericCols.length) {
    const grand: Row = { [columns[0].key]: FALLBACK.grandTotalLabel };
    numericCols.forEach((col) => { grand[col.key] = aggField(safeRows, col.key, mode); });
    modelRows.push(rowFrom(grand, columns, capabilities, rules, 'grandtotal'));
  }
  return { columns, rows: modelRows, empty: !modelRows.length, shape: 'grouped' };
}

// ─────────────────────────────────────────────────────────────────────────────
//  TREE GRID — self-referential parent/child hierarchy (client-side tree)
// ─────────────────────────────────────────────────────────────────────────────
export function buildTreeGridModel(safeRows: Row[], capabilities: TableMatrixCapabilities): TableModel {
  const rules = Array.isArray(capabilities.conditionalRules) ? capabilities.conditionalRules : [];
  const dw = defWidth(capabilities);
  const idField = capabilities.treeIdField || '';
  const parentField = capabilities.treeParentField || '';
  const dataCols = Array.isArray(capabilities.columns) && capabilities.columns.length
    ? capabilities.columns.map((c) => c.field)
    : (safeRows[0] ? Object.keys(safeRows[0]) : []);
  if (!idField || !parentField || !safeRows.length || !dataCols.length) {
    return { columns: [], rows: [], empty: true, shape: 'treegrid' };
  }
  const columns: TableModelColumn[] = dataCols.map((f, i) =>
    makeColumn(f, { frozen: i === 0 && capabilities.freezeFirstColumn === true }, safeRows[0]?.[f], dw));

  const childrenOf: Record<string, Row[]> = {};
  const ids = new Set(safeRows.map((r) => String(r[idField] ?? '')));
  safeRows.forEach((r) => {
    const p = String(r[parentField] ?? '');
    const parent = ids.has(p) ? p : '__root';
    (childrenOf[parent] ||= []).push(r);
  });
  const modelRows: TableModelRow[] = [];
  const emit = (parentId: string, depth: number, parentKey: string) => {
    (childrenOf[parentId] || []).forEach((r) => {
      const id = String(r[idField] ?? '');
      const kids = childrenOf[id] || [];
      const key = parentKey ? `${parentKey}${id}` : id;
      modelRows.push(rowFrom(r, columns, capabilities, rules, 'data', {
        depth, expandable: kids.length > 0, expanded: capabilities.groupsExpanded !== false, rowKey: key, parentKey: parentKey || undefined,
      }));
      if (kids.length) emit(id, depth + 1, key);
    });
  };
  emit('__root', 0, '');
  return { columns, rows: modelRows, empty: !modelRows.length, shape: 'treegrid' };
}

// ─────────────────────────────────────────────────────────────────────────────
//  FINANCIAL STATEMENT — indented line items with role-based formatting
// ─────────────────────────────────────────────────────────────────────────────
export function buildFinancialModel(safeRows: Row[], capabilities: TableMatrixCapabilities): TableModel {
  const rules = Array.isArray(capabilities.conditionalRules) ? capabilities.conditionalRules : [];
  const dw = defWidth(capabilities);
  const roleField = capabilities.roleField || '';
  const indentField = capabilities.indentField || '';
  const dataCols = (Array.isArray(capabilities.columns) && capabilities.columns.length
    ? capabilities.columns.map((c) => c.field)
    : (safeRows[0] ? Object.keys(safeRows[0]) : []))
    .filter((f) => f !== roleField && f !== indentField);
  if (!safeRows.length || !dataCols.length) {
    return { columns: [], rows: [], empty: true, shape: 'financial' };
  }
  const columns: TableModelColumn[] = dataCols.map((f, i) =>
    makeColumn(f, { frozen: i === 0 && capabilities.freezeFirstColumn === true }, safeRows[0]?.[f], dw));

  const modelRows: TableModelRow[] = safeRows.map((r) => {
    const role = String((roleField && r[roleField]) || 'item').toLowerCase();
    const kind: TableModelRow['kind'] = role === 'header' ? 'header'
      : role === 'subtotal' ? 'subtotal'
      : role === 'total' ? 'total'
      : 'data';
    const depth = indentField ? Math.max(0, Math.floor(toNum(r[indentField]) ?? 0)) : 0;
    return rowFrom(r, columns, capabilities, rules, kind, { role, depth });
  });
  return { columns, rows: modelRows, empty: !modelRows.length, shape: 'financial' };
}
